import React, { Fragment, useState, useEffect } from 'react';
import { Form, FormGroup, Input, Label } from 'reactstrap';
import { Btn, P } from '../../../AbstractElements';
import {
  EmailAddress,
  ForgotPassword,
  Password,
  SignIn
} from '../../../Constant';
import { Link, useNavigate } from 'react-router-dom';
import { loginMerchant, merchantProfile } from '../../../api-handle/authApi';
import { isAuthenticated, setSession } from '../../../api-handle/authService';
import OtherWay from './OtherWay';
import FitfindLogo from "../../../../src/assets/images/fitfind-logo.png";
import { Eye, EyeOff } from 'lucide-react';

const LoginTab = ({ selected }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [togglePassword, setTogglePassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const navigate = useNavigate();

  // If an already-authenticated user lands on the login page, send them to the
  // dashboard instead of showing the form. (Previously this did
  // `localStorage.clear()` on every mount, which silently logged people out.)
  useEffect(() => {
    if (isAuthenticated()) {
      navigate('/pages/dashboard', { replace: true });
    }
  }, [navigate]);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let hasError = false;

    if (!email) {
      setEmailError('Email is required.');
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address.');
      hasError = true;
    }

    if (!password) {
      setPasswordError('Password is required.');
      hasError = true;
    }

    if (hasError) return;

    try {
      const response = await loginMerchant({ email, password });
      if (response?.token) {
        setSession(response.token, response.refreshToken);
        localStorage.setItem('login', JSON.stringify(true));
        
        // Check merchant status before redirecting
        try {
          const profileRes = await merchantProfile();
          if (profileRes?.isSuccess && profileRes?.user?.userType === 'merchant') {
            const merchant = profileRes.user.merchant;
            if (merchant && merchant.status === 'pending') {
              // Don't redirect to dashboard for pending merchants
              navigate('/merchant/profile');
              return;
            }
          }
        } catch (profileError) {
          console.error('Error fetching profile after login:', profileError);
          // Continue to dashboard if profile fetch fails
        }
        
        navigate('/pages/dashboard');
      } else {
        setErrorMessage('Login failed: No token received.');
      }
    } catch (error) {
      setErrorMessage(error.message || 'Login failed.');
    }
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
    if (errorMessage) setErrorMessage('');
  };

  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError('');
    if (errorMessage) setErrorMessage('');
  };

  return (
    <Fragment>
      <div>
        <div>
          <div style={{ textAlign: 'center', marginBottom: '20px' }}>
            <img src={FitfindLogo} alt="Fitfind Logo" style={{ maxHeight: '80px' }} />
          </div>

          <Form onSubmit={handleSubmit} style={{ width: '100%' }}>
            <P style={{ textAlign: 'center', marginBottom: '20px' }}>
              Enter your email & password to login
            </P>

            {errorMessage && (
              <p style={{ color: 'red', textAlign: 'center', marginBottom: '15px' }}>
                {errorMessage}
              </p>
            )}

            <FormGroup style={{ marginBottom: '20px' }}>
              <Label style={{ fontWeight: '600' }}>{EmailAddress}</Label>
              <Input
                type="email"
                placeholder="xyz@mail.com"
                value={email}
                onChange={handleEmailChange}
                style={{
                  height: '45px',
                  fontSize: '14px',
                  borderColor: emailError ? 'red' : '#ced4da',
                  boxShadow: emailError ? '0 0 0 0.2rem rgba(255,0,0,.1)' : 'none'
                }}
              />
              {emailError && (
                <p style={{ color: 'red', fontSize: '13px', marginTop: '5px' }}>
                  {emailError}
                </p>
              )}
            </FormGroup>

            <FormGroup style={{ marginBottom: '10px' }}>
              <Label style={{ fontWeight: '600' }}>{Password}</Label>
              <div style={{ position: 'relative' }}>
                <Input
                  type={togglePassword ? 'text' : 'password'}
                  placeholder="********"
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                  style={{
                    height: '45px',
                    fontSize: '14px',
                    paddingRight: '40px',
                    borderColor: passwordError ? 'red' : '#ced4da',
                    boxShadow: passwordError ? '0 0 0 0.2rem rgba(255,0,0,.1)' : 'none'
                  }}
                />
                <span
                  onClick={() => setTogglePassword(!togglePassword)}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'pointer',
                    color: '#007bff'
                  }}
                >
                  {togglePassword ? <EyeOff size={16} color='black' /> : <Eye size={16} color='black' />}
                </span>
              </div>
              {passwordError && (
                <p style={{ color: 'red', fontSize: '13px', marginTop: '5px' }}>
                  {passwordError}
                </p>
              )}
            </FormGroup>

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginBottom: '20px'
            }}>
              <Link
                to="/pages/authentication/forget-password/"
                style={{
                  fontSize: '13px',
                  color: 'black',
                  textDecoration: 'none'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {ForgotPassword}
              </Link>
            </div>

            {selected === 'simpleLogin' && (
              <Btn
                attrBtn={{
                  type: 'submit',
                  color: 'primary',
                  className: 'd-block w-100',
                  style: {
                    padding: '12px 0',
                    fontSize: '15px',
                    borderRadius: '5px'
                  }
                }}
              >
                {SignIn}
              </Btn>
            )}

            <div style={{ marginTop: '20px' }}>
              <OtherWay />
            </div>
          </Form>
        </div>
      </div>
    </Fragment>
  );
};

export default LoginTab;
